import { ResponsiveModal } from "@components/responsive-modal";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useImageActions } from "@features/images/use-images-actions";
import {
  ArrowDownCircleIcon,
  ArrowsPointingOutIcon,
  HandThumbDownIcon,
  HeartIcon,
} from "@heroicons/react/16/solid";
import { IconButton } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StudioBackground,
  StudioCreateVideo,
  StudioCropExtend,
  StudioEditObjects,
  StudioFace,
  StudioPhotoEffects,
  StudioUpscale,
} from "./actions";
import { STUDIO_ACTIONS } from "./studio-actions-config";
import { shouldShowActionGrid } from "./studio-actions-utils";

interface StudioMobileActionsProps {
  image: ImagesType;
  lockedActions?: string[];
  onLockedClick?: () => void;
}

export const StudioMobileActions = ({
  image,
  lockedActions = [],
  onLockedClick,
}: StudioMobileActionsProps) => {
  const { t } = useTranslation();
  const { restore, remove, download, setPendingUpdate } = useImage(image.id);
  const { toggleFavorite } = useImageActions(image.id);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  const quickActions = [
    {
      id: "download",
      icon: ArrowDownCircleIcon,
      label: t("components.camera-roll-image.download"),
      color: "green" as const,
      onClick: () => download(),
    },
    {
      id: "favorite",
      icon: HeartIcon,
      label: image.favorite
        ? t("components.camera-roll-image.unfavorite")
        : t("components.camera-roll-image.favorite"),
      color: image.favorite ? ("red" as const) : ("gray" as const),
      variant: image.favorite ? ("solid" as const) : ("soft" as const),
      onClick: () => toggleFavorite(image, setPendingUpdate),
    },
    {
      id: "archive",
      icon: HandThumbDownIcon,
      label: t("components.camera-roll-image.archive"),
      color: image.archived ? ("orange" as const) : ("gray" as const),
      variant: image.archived ? ("solid" as const) : ("soft" as const),
      onClick: () => (image.archived ? restore() : remove()),
    },
    ...(image.type !== "video"
      ? [
          {
            id: "upscale",
            icon: ArrowsPointingOutIcon,
            label: t("studio.upscale.title"),
            color: "blue" as const,
            onClick: () => setActiveModal("upscale"),
          },
        ]
      : []),
  ];

  const componentMap: Record<string, any> = {
    background: StudioBackground,
    "crop-extend": StudioCropExtend,
    "photo-effects": StudioPhotoEffects,
    "edit-objects": StudioEditObjects,
    face: StudioFace,
    "create-video": StudioCreateVideo,
  };

  const toolActions = STUDIO_ACTIONS.filter(
    (action) => action.id !== "information"
  ).map((action) => ({
    id: action.id,
    icon: action.icon,
    label: t(action.labelKey),
    component: componentMap[action.id],
    locked: lockedActions.includes(action.id),
  }));

  const handleToolClick = (actionId: string, isLocked: boolean) => {
    if (isLocked) {
      onLockedClick?.();
    } else {
      setActiveModal(actionId);
    }
  };

  const handleModalClose = () => {
    setActiveModal(null);
  };

  const ActiveComponent = toolActions.find(
    (a) => a.id === activeModal
  )?.component;

  // Only show tool actions if action grid should be visible
  const showToolActions = shouldShowActionGrid(image);

  const allActions = [
    ...quickActions,
    ...(showToolActions
      ? toolActions
          .filter((a) => a.id !== "crop-extend" && a.id !== "edit-objects")
          .map((action) => ({
            ...action,
            onClick: () => handleToolClick(action.id, action.locked),
          }))
      : []),
  ];

  const activeAction = allActions.find((a) => a.id === activeModal);

  return (
    <>
      {/* All Actions - Bottom Scrollable Bar */}
      <div className="absolute bottom-4 left-0 right-0 z-20 pointer-events-none">
        <div className="overflow-x-auto scrollbar-hide pointer-events-auto">
          <div className="flex gap-1 px-4 pb-2">
            {allActions.map((action) => {
              const Icon = action.icon;
              return (
                <div
                  key={action.id}
                  className="flex flex-col items-center gap-1 flex-shrink-0"
                >
                  <IconButton
                    size="3"
                    variant={(action as any).variant || "solid"}
                    color={(action as any).color}
                    className="rounded-full shadow-lg"
                    onClick={action.onClick}
                  >
                    <Icon className="w-5 h-5" />
                  </IconButton>
                  <span className="text-xs text-center w-[60px] leading-tight truncate">
                    {action.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modals for each tool */}
      {activeModal === "upscale" && (
        <ResponsiveModal
          isOpen={true}
          onClose={handleModalClose}
          title={quickActions.find((a) => a.id === "upscale")?.label || ""}
          height="auto"
        >
          <StudioUpscale image={image} />
        </ResponsiveModal>
      )}

      {ActiveComponent && activeModal !== "upscale" && (
        <ResponsiveModal
          isOpen={true}
          onClose={handleModalClose}
          title={activeAction?.label || ""}
          height="auto"
        >
          <ActiveComponent image={image} onAction={handleModalClose} />
        </ResponsiveModal>
      )}
    </>
  );
};
